import { Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select } from "@mui/material"
import { ScheduleDay, ScheduleDaysDefinition, ScheduleDefinitionBase, ScheduleDefinitionType, ScheduleMonth, ScheduleMonthDefinition, ScheduleMonthMode, ScheduleQuarter, ScheduleQuarterDefinition, ScheduleQuarterMode, ScheduleTimePeriod, ScheduleTimePeriodsDefinition, ScheduleWeek, ScheduleWeekDefinition, ScheduleWeekMode } from "../../../../../types/actionModule/schedulingStrategy/schedulingStrategyType"
import { useEffect, useState } from "react";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ScheduleDaysDefinitionEditor } from "./ScheduleDaysDefinitionEditor";
import { ScheduleWeeksDefinitionEditor } from "./ScheduleWeeksDefinitionEditor";
import { ScheduleMonthsDefinitionEditor } from "./ScheduleMonthsDefinitionEditor";
import { ScheduleQuarterDefinitionEditor } from "./ScheduleQuarterDefinitionEditor";
import { ScheduleTimeDefinitionEditor } from "./ScheduleTimeDefinitionEditor";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

interface Props {
    scheduleDefinition: ScheduleDefinitionBase;
    onUpdateScheduleDefinition: (scheduleDefinition: ScheduleDefinitionBase) => void;
}

export default function ScheduleDefinitionEditor(props: Props) {
    const { t } = useTranslation();

    const [schedulingType, setSchedulingType] = useState<ScheduleDefinitionType>(ScheduleDefinitionType.Day);
    const [daySchedule, setDaySchedule] = useState<ScheduleDay[]>([]);
    const [weekSchedule, setWeekSchedule] = useState<ScheduleWeek>({
        mode: ScheduleWeekMode.None,
        value: 0
    });
    const [monthSchedule, setMonthSchedule] = useState<ScheduleMonth>({
        mode: ScheduleMonthMode.None,
        value: 0,
        weekOffsetInsideMonth: 0,
    });
    const [quarterSchedule, setQuarterSchedule] = useState<ScheduleQuarter>({
        mode: ScheduleQuarterMode.None,
        value: 0,
        monthOffsetInsideQuarter: 0,
    });
    const [timeModel, setTimeModel] = useState<ScheduleTimePeriod[]>([]);

    const [scheduleDefinition, setScheduleDefinition] = useState<ScheduleDefinitionBase>({
        id: 0,
        discriminator: ScheduleDefinitionType.Day,
        combineConsecutiveDays: false,
        separateByMonth: false,
        separateByWeek: false,
        startDate: null,
        endDate: null,
    })

    useEffect(() => {
        let result: ScheduleDefinitionBase;

        const week = {
            ...weekSchedule,
            days: daySchedule
        } as ScheduleWeek;

        const month = {
            ...monthSchedule,
            week: week,
        } as ScheduleMonth;

        const quarter = {
            ...quarterSchedule,
            month: month,
        } as ScheduleQuarter;

        switch (schedulingType) {
            case ScheduleDefinitionType.Time:
                result = {
                    ...scheduleDefinition,
                    discriminator: schedulingType,
                    timePeriods: timeModel,
                } as ScheduleTimePeriodsDefinition;
                break;
            case ScheduleDefinitionType.Day:
                result = {
                    ...scheduleDefinition,
                    discriminator: schedulingType,
                    days: daySchedule,
                } as ScheduleDaysDefinition;
                break;
            case ScheduleDefinitionType.Week:
                result = {
                    ...scheduleDefinition,
                    discriminator: schedulingType,
                    week: week,
                } as ScheduleWeekDefinition;
                break;
            case ScheduleDefinitionType.Month:
                result = {
                    ...scheduleDefinition,
                    discriminator: schedulingType,
                    month: month,
                } as ScheduleMonthDefinition;
                break;
            case ScheduleDefinitionType.Quarter:
                result = {
                    ...scheduleDefinition,
                    discriminator: schedulingType,
                    quarter: quarter,
                } as ScheduleQuarterDefinition;
                break;
        }

        result.startDate = result.startDate ? dayjs(result.startDate).format("YYYY-MM-DD") : null;
        result.endDate = result.endDate ? dayjs(result.endDate).format("YYYY-MM-DD") : null;

        props.onUpdateScheduleDefinition(result);
    }, [schedulingType, daySchedule, weekSchedule, monthSchedule, quarterSchedule, timeModel, scheduleDefinition]);

    return <>
        <FormControl sx={{ minWidth: 250, flex: 1, mt: 2 }}>
            <InputLabel>{t("actionModules.schedulingStrategy.baseMode")}</InputLabel>
            <Select
                value={schedulingType}
                size="small"
                fullWidth
                label={t("actionModules.schedulingStrategy.baseMode")}
                onChange={(v) => setSchedulingType(v.target.value as ScheduleDefinitionType)}
            >
                <MenuItem value={ScheduleDefinitionType.Day}>{t("actionModules.schedulingStrategy.scheduleDefinition.days.name")}</MenuItem>
                <MenuItem value={ScheduleDefinitionType.Week}>{t("actionModules.schedulingStrategy.scheduleDefinition.weeks.name")}</MenuItem>
                <MenuItem value={ScheduleDefinitionType.Month}>{t("actionModules.schedulingStrategy.scheduleDefinition.months.name")}</MenuItem>
                <MenuItem value={ScheduleDefinitionType.Quarter}>{t("actionModules.schedulingStrategy.scheduleDefinition.quarter.name")}</MenuItem>
                <MenuItem value={ScheduleDefinitionType.Time}>{t("actionModules.schedulingStrategy.scheduleDefinition.time.name")}</MenuItem>
            </Select>
        </FormControl>

        <FormGroup sx={{ flexDirection: "row", gap: 2 }}>
            <DatePicker
                value={scheduleDefinition.startDate ? dayjs(scheduleDefinition.startDate) : null}
                onChange={(e) => {
                    setScheduleDefinition({
                        ...scheduleDefinition,
                        startDate: e?.toDate() ?? null,
                    });
                }}
                sx={{ minWidth: 100, flex: 1 }}
                label={t("actionModules.schedulingStrategy.scheduleDefinition.start")}></DatePicker>
            <DatePicker
                value={scheduleDefinition.endDate ? dayjs(scheduleDefinition.endDate) : null}
                onChange={(e) => {
                    setScheduleDefinition({
                        ...scheduleDefinition,
                        endDate: e?.toDate() ?? null,
                    });
                }}
                sx={{ minWidth: 100, flex: 1 }}
                label={t("actionModules.schedulingStrategy.scheduleDefinition.end")}></DatePicker>
        </FormGroup>

        <FormGroup>
            <FormControlLabel
                control={<Checkbox
                    checked={scheduleDefinition.combineConsecutiveDays}
                    onChange={(e) => setScheduleDefinition({
                        ...scheduleDefinition,
                        combineConsecutiveDays: e.target.checked,
                    })} />}
                label={t("actionModules.schedulingStrategy.scheduleDefinition.combineConsecutiveDays")}
            />
            <FormControlLabel
                control={<Checkbox
                    checked={scheduleDefinition.separateByWeek}
                    onChange={(e) => setScheduleDefinition({
                        ...scheduleDefinition,
                        separateByWeek: e.target.checked,
                    })} />}
                label={t("actionModules.schedulingStrategy.scheduleDefinition.separateByWeek")}
            />
            <FormControlLabel
                control={<Checkbox
                    checked={scheduleDefinition.separateByMonth}
                    onChange={(e) => setScheduleDefinition({
                        ...scheduleDefinition,
                        separateByMonth: e.target.checked,
                    })} />}
                label={t("actionModules.schedulingStrategy.scheduleDefinition.separateByMonth")}
            />
        </FormGroup>

        {
            schedulingType === ScheduleDefinitionType.Day && <ScheduleDaysDefinitionEditor
                dayModel={daySchedule}
                onUpdateDayModel={(v) => setDaySchedule(v)}
            />
        }
        {
            schedulingType === ScheduleDefinitionType.Week && <ScheduleWeeksDefinitionEditor
                dayModel={daySchedule}
                onUpdateDayModel={(v) => setDaySchedule(v)}
                weekModel={weekSchedule}
                onUpdateWeekModel={(v) => setWeekSchedule(v)}
            />
        }
        {
            schedulingType === ScheduleDefinitionType.Month && <ScheduleMonthsDefinitionEditor
                dayModel={daySchedule}
                onUpdateDayModel={(v) => setDaySchedule(v)}
                weekModel={weekSchedule}
                onUpdateWeekModel={(v) => setWeekSchedule(v)}
                monthModel={monthSchedule}
                onUpdateMonthModel={(v) => setMonthSchedule(v)}
            />
        }
        {
            schedulingType === ScheduleDefinitionType.Quarter && <ScheduleQuarterDefinitionEditor
                dayModel={daySchedule}
                onUpdateDayModel={(v) => setDaySchedule(v)}
                weekModel={weekSchedule}
                onUpdateWeekModel={(v) => setWeekSchedule(v)}
                monthModel={monthSchedule}
                onUpdateMonthModel={(v) => setMonthSchedule(v)}
                quarterModel={quarterSchedule}
                onUpdateQuarterModel={(v) => setQuarterSchedule(v)}
            />
        }
        {
            schedulingType === ScheduleDefinitionType.Time && <ScheduleTimeDefinitionEditor
                timeModel={timeModel}
                onUpdateTimeModel={(v) => setTimeModel(v)}
            />
        }
    </>
}
